import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useConstants } from '@/contexts/ConstantsContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Phone, MessageSquare, Linkedin, Mail, StickyNote, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const activityIcons = {
  'Cold Call': Phone,
  'Callback': Phone,
  'WhatsApp': MessageSquare,
  'LinkedIn Message': Linkedin,
  'SMS': MessageSquare,
  'Email (manual)': Mail,
  'Note': StickyNote,
};

const QuickLogModal = ({ open, onOpenChange, leadId = null, lead = null, onSuccess }) => {
  const navigate = useNavigate();
  const { activity_types, call_outcomes, call_windows, reached_who_options, decision_maker_status } = useConstants();
  
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState(leadId || '');
  const [selectedLead, setSelectedLead] = useState(lead);
  const [activityType, setActivityType] = useState('Cold Call');
  const [callOutcome, setCallOutcome] = useState('');
  const [callWindow, setCallWindow] = useState('');
  const [reachedWho, setReachedWho] = useState('');
  const [dmStatus, setDmStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [meetingDatetime, setMeetingDatetime] = useState('');
  const [saving, setSaving] = useState(false);

  const isCallActivity = activityType === 'Cold Call' || activityType === 'Callback';

  useEffect(() => {
    if (open && !leadId) {
      fetchLeads();
    }
  }, [open, leadId]);

  useEffect(() => {
    if (leadId) {
      setSelectedLeadId(leadId);
    }
    if (lead) {
      setSelectedLead(lead);
    }
  }, [leadId, lead]);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data.filter(l => l.stage !== 'Closed Won' && l.stage !== 'Closed Lost'));
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const resetForm = () => {
    if (!leadId) {
      setSelectedLeadId('');
      setSelectedLead(null);
    }
    setActivityType('Cold Call');
    setCallOutcome('');
    setCallWindow('');
    setReachedWho('');
    setDmStatus('');
    setNotes('');
    setMeetingDatetime('');
  };

  const handleSubmit = async () => {
    const targetLeadId = selectedLeadId || leadId;
    if (!targetLeadId) {
      toast.error('Please select a lead');
      return;
    }

    if (isCallActivity && !callOutcome) {
      toast.error('Please select a call outcome');
      return;
    }

    // Check Do Not Call
    const targetLead = selectedLead || leads.find(l => l.id === targetLeadId);
    if (targetLead?.do_not_call && isCallActivity) {
      toast.error('This lead is marked as Do Not Call');
      return;
    }

    setSaving(true);
    try {
      const activityData = {
        lead_id: targetLeadId,
        activity_type: activityType,
        outcome_notes: notes || null,
      };

      if (isCallActivity) {
        activityData.call_outcome = callOutcome;
        activityData.call_window = callWindow || null;
        activityData.reached_who = reachedWho || null;
        activityData.decision_maker_status = dmStatus || null;
        if (callOutcome === 'Appointment scheduled' && meetingDatetime) {
          activityData.meeting_datetime = meetingDatetime;
        }
      }

      await axios.post(`${API}/activities`, activityData);
      toast.success('Activity logged successfully');
      resetForm();
      onOpenChange(false);
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log activity');
    } finally {
      setSaving(false);
    }
  };

  const quickOutcomeButtons = [
    { value: 'No answer', label: 'No Answer', color: 'bg-zinc-600' },
    { value: 'Voicemail', label: 'Voicemail', color: 'bg-zinc-600' },
    { value: 'Gatekeeper answered', label: 'Gatekeeper', color: 'bg-amber-600' },
    { value: 'Decision maker answered', label: 'DM Answered', color: 'bg-emerald-600' },
    { value: 'Appointment scheduled', label: 'Booked!', color: 'bg-primary' },
    { value: 'Not interested', label: 'Not Interested', color: 'bg-red-600' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border max-h-[90vh] flex flex-col" data-testid="quick-log-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold">Log Activity</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 mt-4 pr-2">
          {/* Lead Selection (if not pre-selected) */}
          {!leadId && (
            <div className="space-y-2">
              <Label>Select Lead</Label>
              <Select value={selectedLeadId} onValueChange={(v) => {
                setSelectedLeadId(v);
                setSelectedLead(leads.find(l => l.id === v));
              }}>
                <SelectTrigger data-testid="select-lead">
                  <SelectValue placeholder="Choose a lead..." />
                </SelectTrigger>
                <SelectContent>
                  {leads.map(l => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.full_name} - {l.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Show selected lead info */}
          {(selectedLead || lead) && (
            <div className="p-3 bg-secondary/50 rounded-lg border border-border">
              <p className="font-semibold">{(selectedLead || lead).full_name}</p>
              <p className="text-sm text-muted-foreground">{(selectedLead || lead).company_name}</p>
              <p className="text-sm text-muted-foreground font-mono">{(selectedLead || lead).phone}</p>
            </div>
          )}

          {/* Activity Type */}
          <div className="space-y-2">
            <Label>Activity Type</Label>
            <div className="flex flex-wrap gap-2">
              {activity_types.map(type => {
                const Icon = activityIcons[type] || StickyNote;
                const isSelected = activityType === type;
                return (
                  <Button
                    key={type}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    className={`quick-log-btn ${isSelected ? '' : 'border-border'}`}
                    onClick={() => setActivityType(type)}
                    data-testid={`activity-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {type}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Call Outcome (for calls) */}
          {isCallActivity && (
            <>
              <div className="space-y-2">
                <Label>Call Outcome *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {quickOutcomeButtons.map(btn => (
                    <Button
                      key={btn.value}
                      variant="outline"
                      className={`quick-log-btn h-12 ${
                        callOutcome === btn.value 
                          ? `${btn.color} text-white border-transparent` 
                          : 'border-border'
                      }`}
                      onClick={() => setCallOutcome(btn.value)}
                      data-testid={`outcome-${btn.value.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {btn.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Meeting datetime if appointment scheduled */}
              {callOutcome === 'Appointment scheduled' && (
                <div className="space-y-2">
                  <Label>Meeting Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={meetingDatetime}
                    onChange={(e) => setMeetingDatetime(e.target.value)}
                    className="bg-secondary border-border"
                    data-testid="meeting-datetime"
                  />
                </div>
              )}

              {/* Call Window */}
              <div className="space-y-2">
                <Label>Call Window</Label>
                <div className="flex gap-2">
                  {call_windows.map(window => (
                    <Button
                      key={window}
                      variant={callWindow === window ? 'default' : 'outline'}
                      size="sm"
                      className={callWindow === window ? '' : 'border-border'}
                      onClick={() => setCallWindow(window)}
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      {window}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Reached Who */}
              <div className="space-y-2">
                <Label>Reached Who</Label>
                <Select value={reachedWho} onValueChange={setReachedWho}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue placeholder="Who answered?" />
                  </SelectTrigger>
                  <SelectContent>
                    {reached_who_options.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* DM Status */}
              <div className="space-y-2">
                <Label>Decision Maker Status</Label>
                <Select value={dmStatus} onValueChange={setDmStatus}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue placeholder="DM status..." />
                  </SelectTrigger>
                  <SelectContent>
                    {decision_maker_status.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes..."
              className="bg-secondary border-border resize-none"
              rows={2}
              data-testid="activity-notes"
            />
          </div>
        </div>

        {/* Submit - Fixed at bottom, always visible */}
        <div className="flex-shrink-0 pt-4 border-t border-border mt-4">
          <Button
            className="w-full h-12 text-lg font-semibold"
            onClick={handleSubmit}
            disabled={saving}
            data-testid="save-activity-btn"
          >
            {saving ? 'Saving...' : 'Save Activity'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickLogModal;
